import { c as _c } from "react/compiler-runtime";
import type { Color } from '@onefootprint/design-tokens';
import type { Entity } from '@onefootprint/request-types/dashboard';
import { useState } from 'react';
import type { DeviceInsightEvent } from '@/components/entities/components/details/hooks/use-entity-device-insights';
import { icoForUserAgent } from '@/utils/user-agent';
import type { MapMarkerProps } from '../../map/components/map-marker';
import AddressType from '../components/address-card/types';
import getIconForAddress from '../utils/get-icon-for-address';
import useAddressCoordinates from './use-address-coordinates';
import type { MultiSelectOption, MultiSelectOptionValue } from './use-multi-select-options';
type Entry = {
  id: string; // unique id
  type: MultiSelectOptionValue;
  coordinates?: {
    lat: number;
    lng: number;
  };
  data?: DeviceInsightEvent;
  cardisPending?: boolean;
  marker?: MapMarkerProps;
};
const useEntries = (entity, deviceInsights, options) => {
  const $ = _c(46);
  const {
    data: businessData,
    isPending: businessCoordLoading,
    isError: businessError
  } = useAddressCoordinates(entity, AddressType.business);
  const {
    data: residentialData,
    isPending: residentialCoordLoading,
    isError: residentialError
  } = useAddressCoordinates(entity, AddressType.residential);
  let t0;
  if ($[0] !== options) {
    t0 = new Set(options.map(_temp));
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const selectedOptionsSet = t0;
  let innerEntries;
  if ($[2] !== businessCoordLoading || $[3] !== businessData || $[4] !== businessError || $[5] !== deviceInsights || $[6] !== residentialCoordLoading || $[7] !== residentialData || $[8] !== residentialError || $[9] !== selectedOptionsSet) {
    innerEntries = {};
    deviceInsights.filter(insight => selectedOptionsSet.has(insight.event.data.kind)).forEach(event => {
      const {
        data: t1
      } = event.event;
      const {
        insight: insight_0,
        kind
      } = t1;
      const lat = insight_0.latitude;
      const lng = insight_0.longitude;
      const hasCoords = !!lat && !!lng;
      const id = `${insight_0.timestamp}-${lat}-${lng}-${kind}`;
      innerEntries[id] = {
        id,
        type: kind,
        coordinates: hasCoords ? {
          lat,
          lng
        } : undefined,
        data: event,
        marker: hasCoords ? {
          id,
          lat,
          lng,
          getIcon: color => icoForUserAgent(insight_0?.userAgent ?? "", color ?? "quinary", "small")
        } : undefined
      };
    });
    const isBusinessAddressSelected = selectedOptionsSet.has("businessAddress");
    if (isBusinessAddressSelected) {
      let t2;
      if ($[11] !== businessData) {
        t2 = businessData || {};
        $[11] = businessData;
        $[12] = t2;
      } else {
        t2 = $[12];
      }
      const {
        lat: businessLat,
        lng: businessLng
      } = t2;
      const hasCoords_0 = businessLat !== undefined && businessLng !== undefined;
      let t3;
      if ($[13] !== businessLat || $[14] !== businessLng || $[15] !== hasCoords_0) {
        t3 = hasCoords_0 ? {
          lat: businessLat,
          lng: businessLng
        } : undefined;
        $[13] = businessLat;
        $[14] = businessLng;
        $[15] = hasCoords_0;
        $[16] = t3;
      } else {
        t3 = $[16];
      }
      const t4 = businessCoordLoading && !businessError;
      let t5;
      if ($[17] !== businessLat || $[18] !== businessLng || $[19] !== hasCoords_0) {
        t5 = hasCoords_0 ? {
          id: "business",
          lat: businessLat,
          lng: businessLng,
          getIcon: _temp2
        } : undefined;
        $[17] = businessLat;
        $[18] = businessLng;
        $[19] = hasCoords_0;
        $[20] = t5;
      } else {
        t5 = $[20];
      }
      let t6;
      if ($[21] !== t3 || $[22] !== t4 || $[23] !== t5) {
        t6 = {
          id: "business",
          type: "businessAddress",
          coordinates: t3,
          cardisPending: t4,
          marker: t5
        };
        $[21] = t3;
        $[22] = t4;
        $[23] = t5;
        $[24] = t6;
      } else {
        t6 = $[24];
      }
      innerEntries.business = t6;
    }
    const isResidentialAddressSelected = selectedOptionsSet.has("residentialAddress");
    if (isResidentialAddressSelected) {
      let t2;
      if ($[25] !== residentialData) {
        t2 = residentialData || {};
        $[25] = residentialData;
        $[26] = t2;
      } else {
        t2 = $[26];
      }
      const {
        lat: residentialLat,
        lng: residentialLng
      } = t2;
      const hasCoords_1 = residentialLat !== undefined && residentialLng !== undefined;
      let t3;
      if ($[27] !== hasCoords_1 || $[28] !== residentialLat || $[29] !== residentialLng) {
        t3 = hasCoords_1 ? {
          lat: residentialLat,
          lng: residentialLng
        } : undefined;
        $[27] = hasCoords_1;
        $[28] = residentialLat;
        $[29] = residentialLng;
        $[30] = t3;
      } else {
        t3 = $[30];
      }
      const t4 = residentialCoordLoading && !residentialError;
      let t5;
      if ($[31] !== hasCoords_1 || $[32] !== residentialLat || $[33] !== residentialLng) {
        t5 = hasCoords_1 ? {
          id: "residential",
          lat: residentialLat,
          lng: residentialLng,
          getIcon: _temp3
        } : undefined;
        $[31] = hasCoords_1;
        $[32] = residentialLat;
        $[33] = residentialLng;
        $[34] = t5;
      } else {
        t5 = $[34];
      }
      let t6;
      if ($[35] !== t3 || $[36] !== t4 || $[37] !== t5) {
        t6 = {
          id: "residential",
          type: "residentialAddress",
          coordinates: t3,
          cardisPending: t4,
          marker: t5
        };
        $[35] = t3;
        $[36] = t4;
        $[37] = t5;
        $[38] = t6;
      } else {
        t6 = $[38];
      }
      innerEntries.residential = t6;
    }
    $[2] = businessCoordLoading;
    $[3] = businessData;
    $[4] = businessError;
    $[5] = deviceInsights;
    $[6] = residentialCoordLoading;
    $[7] = residentialData;
    $[8] = residentialError;
    $[9] = selectedOptionsSet;
    $[10] = innerEntries;
  } else {
    innerEntries = $[10];
  }
  const entries = innerEntries;
  const [selectedId, setSelectedId] = useState(null);
  const selectedCoords = selectedId ? entries[selectedId]?.coordinates : undefined;
  let t1;
  if ($[39] !== selectedId) {
    t1 = id_2 => {
      if (id_2 === selectedId) {
        setSelectedId(null);
      } else {
        setSelectedId(id_2);
      }
    };
    $[39] = selectedId;
    $[40] = t1;
  } else {
    t1 = $[40];
  }
  const onSelectedIdChange = t1;
  let t2;
  if ($[41] !== entries || $[42] !== onSelectedIdChange || $[43] !== selectedCoords || $[44] !== selectedId) {
    t2 = {
      entries,
      selectedCoords,
      selectedId,
      onSelectedIdChange
    };
    $[41] = entries;
    $[42] = onSelectedIdChange;
    $[43] = selectedCoords;
    $[44] = selectedId;
    $[45] = t2;
  } else {
    t2 = $[45];
  }
  return t2;
};
export default useEntries;
function _temp(option) {
  return option.value;
}
function _temp2(color_0) {
  return getIconForAddress(AddressType.business, color_0, "small");
}
function _temp3(color_1) {
  return getIconForAddress(AddressType.residential, color_1, "small");
}